import { useSyncExternalStore } from "react";
import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

export const AUTH_COOKIE = "Authorization";

const USER_AUTH_PREFIXES = ["CO::", "GO::", "LI::"];

function getCookies() {
	return new Cookies();
}

export function getAuthorizationToken() {
	return getCookies().get(AUTH_COOKIE) || null;
}

export function hasUserAuthPrefix(token = getAuthorizationToken()) {
	return USER_AUTH_PREFIXES.some((prefix) => token?.startsWith(prefix));
}

export function stripAuthorizationPrefix(token = getAuthorizationToken()) {
	if (!token) return null;

	for (const prefix of USER_AUTH_PREFIXES) {
		if (token.startsWith(prefix)) {
			return token.slice(prefix.length);
		}
	}

	return token;
}

export function getAuthorizationTokenExpiration(token = getAuthorizationToken()) {
	const decodedToken = decodeToken(stripAuthorizationPrefix(token));
	if (!decodedToken?.exp) {
		return null;
	}

	return new Date(decodedToken.exp * 1000);
}

export function decodeClaimAuthorizationToken(token = getAuthorizationToken()) {
	if (!token) {
		return null;
	}

	try {
		const decodedToken = decodeToken(token);
		const reviewToken = decodedToken?.data?.review_token;
		const claimPayload = typeof reviewToken === "string" ? JSON.parse(reviewToken) : null;

		if (!claimPayload?.companyId) {
			return null;
		}

		return {
			...claimPayload,
			expiresAt: getAuthorizationTokenExpiration(token)?.toISOString() || null,
		};
	} catch {
		return null;
	}
}

export function isClaimAuthorizationToken(token = getAuthorizationToken()) {
	return Boolean(decodeClaimAuthorizationToken(token)?.companyId);
}

export function isUserAuthorizationToken(token = getAuthorizationToken()) {
	return Boolean(token) && hasUserAuthPrefix(token) && !isClaimAuthorizationToken(token);
}

export function isUserAuthorizationExpired(token = getAuthorizationToken()) {
	if (!isUserAuthorizationToken(token)) {
		return true;
	}

	if (token.startsWith("LI::")) {
		return false;
	}

	try {
		const decodedToken = decodeToken(stripAuthorizationPrefix(token));
		return decodedToken?.exp < Date.now() / 1000;
	} catch {
		return true;
	}
}

export function clearAuthorizationToken() {
	getCookies().remove(AUTH_COOKIE, {
		path: "/",
	});
	notifyAuthorizationChange();
}

export function setAuthorizationToken(token, expiresAt) {
	if (!token) {
		return;
	}

	const expirationDate = expiresAt ? new Date(expiresAt) : getAuthorizationTokenExpiration(token);

	clearAuthorizationToken();
	getCookies().set(AUTH_COOKIE, token, {
		path: "/",
		...(expirationDate ? { expires: expirationDate } : {}),
	});
	notifyAuthorizationChange();
}

/* -------------------------------------------------------------------------------------------- *
 * Reactive credential
 *
 * The credential used to be read straight from the cookie inside render — in six `enabled:` gates
 * across queries.js, in `useIsLoggedIn`, and on the onboarding screen. Nothing re-rendered when the
 * cookie appeared, so a query that resolved "signed out" on first render stayed disabled for the
 * life of the page. React Query reports a disabled query as `isLoading: false, data: undefined`,
 * which reads as "this account is empty" rather than "we never asked" — a signed-in user saw a 0%
 * profile with no request in the network tab and no error anywhere.
 *
 * One store owns the credential now, and every reader subscribes to it.
 * -------------------------------------------------------------------------------------------- */

const listeners = new Set();

/** Call after any write to the cookie so subscribed components re-render. */
export function notifyAuthorizationChange() {
	for (const listener of listeners) listener();
}

function subscribeToAuthorization(listener) {
	listeners.add(listener);

	// A credential can also change outside this tab — a sign-out elsewhere, or devtools. Re-reading
	// when the tab regains focus catches those without polling the cookie on a timer.
	const recheck = () => listener();
	window.addEventListener("focus", recheck);

	return () => {
		listeners.delete(listener);
		window.removeEventListener("focus", recheck);
	};
}

/** Snapshot is a string (or null), so React's identity check is a value comparison. */
function readAuthorizationToken() {
	return getAuthorizationToken();
}

/**
 * The current credential, re-rendering the caller whenever it changes.
 *
 * Use this instead of calling `getAuthorizationToken()` during render.
 */
export function useAuthorizationToken() {
	return useSyncExternalStore(
		subscribeToAuthorization,
		readAuthorizationToken,
		// Server render has no cookie jar.
		() => null,
	);
}

/** True when the credential identifies a signed-in user and has not expired. */
export function hasActiveUserAuthorization(token = getAuthorizationToken()) {
	return isUserAuthorizationToken(token) && !isUserAuthorizationExpired(token);
}
